import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, DatePicker, Error, Table, Warning } from '@fikasio/react-ui-components';
import { pickedDateTimeInUserZone, toPickerDate } from 'utils/date';
import { calculateNotifications } from 'components/notifications/utils';
import { getLateProjects } from 'components/projects/utils';
import ProjectsView from 'components/projects/ProjectsView';
import TasksView from 'components/tasks/TasksView';
import BasePage from 'components/UI/BasePage';
import { FEATURES } from 'services/features/catalog';
import { useFeatureEnabled } from 'services/features/hooks';
import { setPaneContent } from 'services/pane/slice';
import { useGetProjectsQuery, usePatchProjectMutation } from 'services/projects/api';
import { useGetTasksQuery, usePatchTaskMutation } from 'services/tasks/api';
import links from 'utils/links';
import { round } from 'utils/maths';
import { spaLinkProps } from 'utils/spaLink';

const NotificationsPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enabled: tasksEnabled } = useFeatureEnabled(FEATURES.tasks);
  const { data: projects } = useGetProjectsQuery();
  const { data } = useGetTasksQuery({}, { skip: !tasksEnabled });
  const tasks = tasksEnabled ? data?.tasks : undefined;
  const [isProjectDueAtDatepickerOpen, setIsProjectDueAtDatepickerOpen] = useState(false);

  const [patchProject] = usePatchProjectMutation();
  const [patchTask] = usePatchTaskMutation();

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname,
    });
  }, []);

  const notifications = tasks && projects && calculateNotifications(tasks, projects);
  // Projects
  const lateProjects = tasksEnabled
    ? notifications?.lateProjects
    : (projects ? getLateProjects(projects, []) : undefined);
  const lateProjectsCount = lateProjects?.length;
  // Tasks
  const lateTasks = notifications?.lateTasks;
  const tasksDueAfterProjectDue = notifications?.tasksDueAfterProjectDue;
  const tasksWithNoDueDate = notifications?.tasksWithNoDueDate;
  const lateTasksCount = lateTasks?.length;
  const overloadInTheFuture = notifications?.overloadInTheFuture;
  const tasksDueAfterProjectDueCount = tasksDueAfterProjectDue?.length;
  const tasksWithNoDueDateCount = tasksWithNoDueDate?.length;

  return (
    <>
      <Helmet>
        <title>{t('notifications')}</title>
      </Helmet>
      <BasePage>
        <Breadcrumb
          items={[
            { ...spaLinkProps(links.paths.home, navigate), label: t('home') },
            { label: t('notifications') },
          ]}
        />
        <h4>{t('notifications')}</h4>
        {
          lateProjectsCount ? (
            <div style={{ margin: '10px 0' }}>
              <Error>
                {t('youHaveXOverdueProjects', { count: lateProjectsCount })}
              </Error>
              <br />
              <ProjectsView
                onProjectClick={
                  projectId => dispatch(
                    setPaneContent({
                      type: 'PROJECT',
                      id: projectId,
                    })
                  )
                }
                projects={lateProjects}
              />
            </div>
          ) : (
            <div style={{ margin: '10px 0' }}>
              {t('youHaveNoOverdueProjects')}
            </div>
          )
        }
        {
          tasksEnabled && lateTasksCount ? (
            <div style={{ margin: '10px 0' }}>
              <Error>
                {t('youHaveXOverdueTasks', { count: lateTasksCount })}
              </Error>
              <br />
              <TasksView
                onTaskClick={
                  taskId => dispatch(
                    setPaneContent({
                      type: 'TASK',
                      id: taskId,
                    })
                  )
                }
                tasks={lateTasks}
              />
            </div>
          ) : tasksEnabled && (
            <div style={{ margin: '10px 0' }}>
              {t('youHaveNoOverdueTasks')}
            </div>
          )
        }
        {
          tasksEnabled && !!tasksWithNoDueDateCount && (
            <div style={{ margin: '10px 0' }}>
              <Warning>
                {t('youHaveXTasksWithNoDueDate', { count: tasksWithNoDueDateCount })}
              </Warning>
              <br />
              <TasksView
                onTaskClick={
                  taskId => dispatch(
                    setPaneContent({
                      type: 'TASK',
                      id: taskId,
                    })
                  )
                }
                tasks={tasksWithNoDueDate}
              />
            </div>
          )
        }
        {
          tasksEnabled && !!tasksDueAfterProjectDueCount && (
            <div style={{ margin: '10px 0' }}>
              <Warning>
                Vous avez <b>{tasksDueAfterProjectDueCount} tâche{tasksDueAfterProjectDueCount > 1 && 's'} qui sont dûes pour une date postérieure à la fin d'un projet dont elles font partie</b>.
              </Warning>
              <br />
              <Table
                bordered
                hover
                responsive
              >
                <thead>
                  <tr>
                    <th>{t('task')}</th>
                    <th style={{ width: 150 }}>{t('dueDate')}</th>
                    <th>{t('project')}</th>
                    <th style={{ width: 150 }}>{t('dueDate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    tasksDueAfterProjectDue
                      .map(({ task, project }) => {
                        return (
                          <tr key={task.id}>
                            <td>{ task && task.description }</td>
                            <td
                              onClick={() => setIsProjectDueAtDatepickerOpen(true)}
                              style={{
                                minWidth: 150,
                              }}
                            >
                              <DatePicker
                                defaultValue={toPickerDate(task.dueAt)}
                                displayFormat="yyyy-MM-dd"
                                isOpen={isProjectDueAtDatepickerOpen}
                                onChange={dueAt => {
                                  const timestamp = pickedDateTimeInUserZone(dueAt)
                                    .set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
                                  patchTask({
                                    id: task.id,
                                    dueAt: timestamp.toJSDate(),
                                  });
                                }}
                                onClose={() => setIsProjectDueAtDatepickerOpen(false)}
                                shouldCloseOnSelect
                                showRemoveValue
                                showTimeSelect={false}
                              />
                            </td>
                            <td>{ project && project.name }</td>
                            <td
                              style={{
                                minWidth: 150,
                              }}
                            >
                              <DatePicker
                                defaultValue={toPickerDate(project.dueAt)}
                                displayFormat="yyyy-MM-dd"
                                onChange={dueAt => {
                                  const timestamp = pickedDateTimeInUserZone(dueAt)
                                    .set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
                                  patchProject({
                                    id: project.id,
                                    dueAt: timestamp.toJSDate(),
                                  });
                                }}
                                shouldCloseOnSelect
                                showRemoveValue
                                showTimeSelect={false}
                              />
                            </td>
                          </tr>
                        );
                      })
                  }
                </tbody>
              </Table>
            </div>
          )
        }
        {
          tasksEnabled && !!overloadInTheFuture && (
            <div style={{ margin: '10px 0' }}>
              <Warning>
                {
                  t('youHaveTooMuchToDo', {
                    avgHours: round(overloadInTheFuture.averageHoursPerDayBeforeDate, 1),
                    date: overloadInTheFuture.date.toISODate(),
                  })
                }
              </Warning>
            </div>
          )
        }
      </BasePage>
    </>
  );
};

export default NotificationsPage;
